import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Add,
  PlaylistAdd,
  Public,
  Lock,
  Close,
  Delete,
  Warning
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { userWatchlistsByUserIDAndCreatedAt, watchlistItemsByWatchlistIDAndCreatedAt } from '../../graphql/queries';
import { createUserWatchlist, deleteUserWatchlist } from '../../graphql/mutations';
import { createWatchlistItemMutation } from '../../graphql/watchlist';
import { enrichWatchlistsWithCounts } from '../../utils/watchlistCountUtils';

const WatchlistSelector = ({ 
  open, 
  onClose, 
  content, 
  contentType, 
  onAddToWatchlist 
}) => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const theme = useTheme();
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlists, setSelectedWatchlists] = useState([]);
  const [existingWatchlists, setExistingWatchlists] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [newWatchlistDescription, setNewWatchlistDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [watchlistToDelete, setWatchlistToDelete] = useState(null);

  useEffect(() => {
    if (open && user) {
      loadUserWatchlists();
    }
  }, [open, user]);

  const loadUserWatchlists = async () => {
    try {
      const client = generateClient();
      const result = await client.graphql({
        query: userWatchlistsByUserIDAndCreatedAt,
        variables: {
          userID: user.id,
          limit: 50
        }
      });
      
      const userWatchlists = result.data?.userWatchlistsByUserIDAndCreatedAt?.items || [];
      
      if (userWatchlists.length === 0) {
        const defaultWatchlist = await createDefaultWatchlist();
        if (defaultWatchlist) {
          userWatchlists.push(defaultWatchlist);
        }
      }
      
      const enrichedWatchlists = await enrichWatchlistsWithCounts(userWatchlists);
      setWatchlists(enrichedWatchlists);
      
      const existing = await checkExistingWatchlists(enrichedWatchlists);
      setExistingWatchlists(existing);
      setSelectedWatchlists([]);
    } catch (error) {
      console.error('Error loading watchlists:', error);
      addNotification({ type: 'error', message: 'Failed to load watchlists' });
    }
  };
  
  const createDefaultWatchlist = async () => {
    try {
      const client = generateClient();
      const CREATE_WATCHLIST_SIMPLE = `
        mutation CreateUserWatchlist($input: CreateUserWatchlistInput!) {
          createUserWatchlist(input: $input) {
            id
            userID
            name
            description
            isDefault
            isPublic
            createdAt
            updatedAt
          }
        }
      `;
      
      const result = await client.graphql({
        query: CREATE_WATCHLIST_SIMPLE,
        variables: {
          input: {
            userID: user.id,
            name: 'Want to Watch',
            description: 'Movies and shows I want to watch',
            isDefault: true,
            isPublic: false
          }
        }
      });
      return result.data.createUserWatchlist;
    } catch (error) {
      console.error('Error creating default watchlist:', error);
      return null;
    }
  };

  const handleDeleteWatchlist = async () => {
    if (!watchlistToDelete) return;
    
    setLoading(true);
    try {
      const client = generateClient();
      await client.graphql({
        query: deleteUserWatchlist,
        variables: {
          input: { id: watchlistToDelete.id }
        }
      });
      
      setWatchlists(prev => prev.filter(w => w.id !== watchlistToDelete.id));
      setDeleteConfirmOpen(false);
      setWatchlistToDelete(null);
      
      addNotification({ 
        type: 'success', 
        message: `Watchlist "${watchlistToDelete.name}" deleted!` 
      });
    } catch (error) {
      console.error('Error deleting watchlist:', error);
      addNotification({ type: 'error', message: 'Failed to delete watchlist' });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteWatchlist = (watchlist) => {
    setWatchlistToDelete(watchlist);
    setDeleteConfirmOpen(true);
  };
  
  const checkExistingWatchlists = async (userWatchlists) => {
    if (contentType === 'MOVIE') {
      try {
        const { getUserMovieInteraction } = await import('../../services/movieInteractionApi');
        const interaction = await getUserMovieInteraction(user.id, content.id);
        
        // If movie is in watchlist, return empty array since we don't track specific watchlists
        // The user can add to any watchlist they want
        return [];
      } catch (error) {
        console.error('Error checking movie interaction:', error);
      }
    }
    
    return [];
  };

  const handleCreateWatchlist = async () => {
    if (!newWatchlistName.trim()) return;
    
    setLoading(true);
    try {
      const client = generateClient();
      const CREATE_WATCHLIST_SIMPLE = `
        mutation CreateUserWatchlist($input: CreateUserWatchlistInput!) {
          createUserWatchlist(input: $input) {
            id
            userID
            name
            description
            isDefault
            isPublic
            createdAt
            updatedAt
          }
        }
      `;
      
      const result = await client.graphql({
        query: CREATE_WATCHLIST_SIMPLE,
        variables: {
          input: {
            userID: user.id,
            name: newWatchlistName.trim(),
            description: newWatchlistDescription.trim() || undefined,
            isDefault: false,
            isPublic
          }
        }
      });
      
      const newWatchlist = result.data.createUserWatchlist;
      setWatchlists(prev => [...prev, newWatchlist]);
      setNewWatchlistName('');
      setNewWatchlistDescription('');
      setIsPublic(false);
      setShowCreateForm(false);
      
      addNotification({ 
        type: 'success', 
        message: `Watchlist "${newWatchlist.name}" created!` 
      });
    } catch (error) {
      console.error('Error creating watchlist:', error);
      addNotification({ type: 'error', message: 'Failed to create watchlist' });
    } finally {
      setLoading(false);
    }
  };

  const handleWatchlistToggle = (watchlistId) => {
    setSelectedWatchlists(prev => 
      prev.includes(watchlistId)
        ? prev.filter(id => id !== watchlistId)
        : [...prev, watchlistId]
    );
  };

  const handleSave = async () => {
    setLoading(true);
    const successfulAdds = [];
    const duplicates = [];
    const errors = [];
    
    try {
      for (const watchlistId of selectedWatchlists) {
        if (existingWatchlists.includes(watchlistId)) {
          const watchlist = watchlists.find(w => w.id === watchlistId);
          duplicates.push(watchlist?.name || 'Unknown');
          continue;
        }
        
        try {
          const watchlist = watchlists.find(w => w.id === watchlistId);
          
          if (contentType === 'MOVIE') {
            // Check if already exists in this specific watchlist
            try {
              const client = generateClient();
              const existingCheck = await client.graphql({
                query: watchlistItemsByWatchlistIDAndCreatedAt,
                variables: {
                  watchlistID: watchlistId,
                  filter: {
                    and: [
                      { movieID: { eq: content.id } },
                      { userID: { eq: user.id } }
                    ]
                  }
                }
              });
              
              const existingItems = existingCheck.data?.watchlistItemsByWatchlistIDAndCreatedAt?.items || [];
              if (existingItems.length > 0) {
                console.log('ℹ️ Movie already exists in this watchlist - skipping');
                const watchlist = watchlists.find(w => w.id === watchlistId);
                duplicates.push(watchlist?.name || 'Unknown');
                continue;
              }
            } catch (checkError) {
              console.warn('⚠️ Error checking existing watchlist items:', checkError);
            }
            
            // Add to UserMovieInteraction table (primary source of truth)
            const { addToWatchlist } = await import('../../services/movieInteractionApi');
            await addToWatchlist(user.id, content.id);
            
            // Also add to legacy WatchlistItem table with proper watchlistID
            try {
              const client = generateClient();
              await client.graphql({
                query: createWatchlistItemMutation,
                variables: {
                  input: {
                    userID: user.id,
                    watchlistID: watchlistId, // Use the selected watchlist ID
                    movieID: content.id,
                    contentType: 'MOVIE',
                    status: 'WANT_TO_WATCH',
                    priority: 5
                  }
                }
              });
              console.log('✅ Added to both UserMovieInteraction and WatchlistItem tables');
            } catch (legacyError) {
              // Don't fail the main operation if legacy sync fails
              console.warn('⚠️ Failed to sync with legacy WatchlistItem table:', legacyError);
            }
          } else {
            console.warn('Show watchlist not implemented yet');
            continue;
          }
          
          successfulAdds.push(`Added to ${watchlist?.name || 'Unknown'}`);
          
          setWatchlists(prev => prev.map(w => 
            w.id === watchlistId 
              ? { ...w, itemCount: (w.itemCount || 0) + 1 }
              : w
          ));
        } catch (itemError) {
          console.error(`Error updating watchlist ${watchlistId}:`, itemError);
          const watchlist = watchlists.find(w => w.id === watchlistId);
          errors.push(watchlist?.name || 'Unknown');
        }
      }
      
      onClose();
      
      if (successfulAdds.length > 0) {
        addNotification({ 
          type: 'success', 
          message: successfulAdds.join(', ')
        });
        
        if (onAddToWatchlist) {
          onAddToWatchlist(selectedWatchlists, content, contentType);
        }
      }
      
      if (duplicates.length > 0) {
        addNotification({ 
          type: 'warning', 
          message: `Already exists in: ${duplicates.join(', ')}` 
        });
      }
      
      if (errors.length > 0) {
        addNotification({ 
          type: 'error', 
          message: `Failed to add to: ${errors.join(', ')}` 
        });
      }
    } catch (error) {
      console.error('Error saving to watchlists:', error);
      addNotification({ type: 'error', message: 'Failed to save to watchlists' });
    } finally {
      setLoading(false);
    }
  };

  const isContentInWatchlist = existingWatchlists.length > 0;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(18, 18, 18, 0.95) 0%, rgba(30, 30, 30, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: theme.palette.mode === 'dark' 
            ? '1px solid rgba(255, 215, 0, 0.2)'
            : '1px solid rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaylistAdd sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6">
            Add to Watchlist
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {isContentInWatchlist && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            icon={<Warning />}
          >
            This {contentType.toLowerCase()} is already in your watchlist
          </Alert>
        )}
        
        <Typography variant="subtitle2" sx={{ mb: 2, opacity: 0.8 }}>
          Select watchlists to add "{content?.title || content?.name}" to:
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          {watchlists.map((watchlist) => {
            const isSelected = selectedWatchlists.includes(watchlist.id);
            const isExisting = existingWatchlists.includes(watchlist.id);
            
            return (
              <Box
                key={watchlist.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  mb: 1,
                  borderRadius: 1,
                  border: `1px solid ${
                    isSelected 
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  backgroundColor: isSelected 
                    ? `${theme.palette.primary.main}15`
                    : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isSelected 
                      ? `${theme.palette.primary.main}25`
                      : `${theme.palette.action.hover}`
                  }
                }}
                onClick={() => !isExisting && handleWatchlistToggle(watchlist.id)}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {watchlist.name}
                    </Typography>
                    {watchlist.isDefault && (
                      <Chip 
                        label="Default" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    )}
                    {watchlist.isPublic ? (
                      <Public sx={{ fontSize: 16, opacity: 0.6 }} />
                    ) : (
                      <Lock sx={{ fontSize: 16, opacity: 0.6 }} />
                    )}
                  </Box>
                  
                  {watchlist.description && (
                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                      {watchlist.description}
                    </Typography>
                  )}
                  
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    {watchlist.itemCount || 0} items
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isExisting && (
                    <Chip 
                      label="Added" 
                      size="small" 
                      color="success" 
                      variant="filled"
                    />
                  )}
                  
                  {!watchlist.isDefault && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteWatchlist(watchlist);
                      }}
                      sx={{ 
                        opacity: 0.6,
                        '&:hover': { 
                          opacity: 1,
                          color: theme.palette.error.main
                        }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {!showCreateForm ? (
          <Button
            startIcon={<Add />}
            onClick={() => setShowCreateForm(true)}
            variant="outlined"
            fullWidth
            sx={{ mb: 1 }}
          >
            Create New Watchlist
          </Button>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Create New Watchlist
            </Typography>
            
            <TextField
              fullWidth
              label="Watchlist Name"
              value={newWatchlistName}
              onChange={(e) => setNewWatchlistName(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              value={newWatchlistDescription}
              onChange={(e) => setNewWatchlistDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
              }
              label="Make this watchlist public"
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleCreateWatchlist}
                variant="contained"
                disabled={!newWatchlistName.trim() || loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Add />}
              >
                Create
              </Button>
              <Button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewWatchlistName('');
                  setNewWatchlistDescription('');
                  setIsPublic(false);
                }}
                variant="outlined"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={selectedWatchlists.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <PlaylistAdd />}
        >
          {loading ? 'Adding...' : `Add to ${selectedWatchlists.length} Watchlist${selectedWatchlists.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Watchlist
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{watchlistToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteWatchlist}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Delete />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default WatchlistSelector;